import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, SearchIcon, CalendarIcon, MessageCircleIcon, GraduationCapIcon, MusicIcon, MenuIcon, XIcon } from 'lucide-react';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { useTheme } from '../providers/ThemeProvider';
interface StudentLayoutProps {
  children: React.ReactNode;
}
export const StudentLayout: React.FC<StudentLayoutProps> = ({
  children
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  // Toggle sidebar on mobile
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  // Open sidebar by default on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    // Set initial state based on screen size
    handleResize();
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return <div className={`w-full min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50] text-white' : 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800'} font-sans transition-colors duration-300`} data-id="element-1548">
      <Navbar setIsDeleteAccountModalOpen={() => {}} data-id="element-1549" />
      <div className="pt-20 pb-16 flex flex-col lg:flex-row" data-id="element-1550">
        {/* Mobile sidebar toggle - increased size and fixed positioning */}
        <button onClick={toggleSidebar} className="lg:hidden fixed bottom-4 right-4 z-30 bg-deep-pink text-white p-4 rounded-full shadow-lg" aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'} style={{
        minWidth: '56px',
        minHeight: '56px'
      }} data-id="element-1551">
          {isSidebarOpen ? <XIcon size={24} data-id="element-1552" /> : <MenuIcon size={24} data-id="element-1553" />}
        </button>
        {/* Left Sidebar - improved mobile layout */}
        <aside className={`w-64 fixed inset-y-0 pt-20 pb-16 ${isDark ? 'bg-navy-900/95' : 'bg-white'} shadow-lg z-20 transform transition-all duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`} data-id="element-1554">
          {/* Close button for mobile sidebar */}
          {isSidebarOpen && <button onClick={toggleSidebar} className="lg:hidden absolute top-24 right-4 bg-deep-pink text-white p-3 rounded-full shadow-md" aria-label="Close sidebar" style={{
          minWidth: '48px',
          minHeight: '48px'
        }} data-id="element-1555">
              <XIcon size={20} data-id="element-1556" />
            </button>}
          <nav className="px-4 py-6" data-id="element-1557">
            <ul className="space-y-3" data-id="element-1558">
              <li data-id="element-1559">
                <Link to="/student-dashboard" className={`flex items-center px-4 py-4 ${location.pathname === '/student-dashboard' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1560">
                  <HomeIcon className="h-6 w-6 mr-3" data-id="element-1561" />
                  <span className="text-base" data-id="element-1562">Dashboard</span>
                </Link>
              </li>
              <li data-id="element-1563">
                <Link to="/find-teachers" className={`flex items-center px-4 py-4 ${location.pathname === '/find-teachers' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1564">
                  <SearchIcon className="h-6 w-6 mr-3" data-id="element-1565" />
                  <span className="text-base" data-id="element-1566">Find Teachers</span>
                </Link>
              </li>
              <li data-id="element-1567">
                <Link to="/student-schedule" className={`flex items-center px-4 py-4 ${location.pathname === '/student-schedule' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1568">
                  <CalendarIcon className="h-6 w-6 mr-3" data-id="element-1569" />
                  <span className="text-base" data-id="element-1570">My Schedule</span>
                </Link>
              </li>
              <li data-id="element-1571">
                <Link to="/messages" className={`flex items-center px-4 py-4 ${location.pathname === '/messages' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1572">
                  <MessageCircleIcon className="h-6 w-6 mr-3" data-id="element-1573" />
                  <span className="text-base" data-id="element-1574">Messages</span>
                </Link>
              </li>
              <li data-id="element-1575">
                <Link to="/online-courses" className={`flex items-center px-4 py-4 ${location.pathname === '/online-courses' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1576">
                  <GraduationCapIcon className="h-6 w-6 mr-3" data-id="element-1577" />
                  <span className="text-base" data-id="element-1578">Courses</span>
                </Link>
              </li>
              <li data-id="element-1579">
                <Link to="/sheet-music" className={`flex items-center px-4 py-4 ${location.pathname === '/sheet-music' ? isDark ? 'text-deep-pink bg-navy-800/50' : 'text-deep-pink bg-red-50' : isDark ? 'text-white hover:bg-navy-800/70' : 'text-gray-700 hover:bg-gray-100'} rounded-lg font-medium transition-colors`} onClick={() => window.innerWidth < 1024 && setIsSidebarOpen(false)} data-id="element-1580">
                  <MusicIcon className="h-6 w-6 mr-3" data-id="element-1581" />
                  <span className="text-base" data-id="element-1582">Sheet Music</span>
                </Link>
              </li>
            </ul>
          </nav>
        </aside>
        {/* Main Content - fixed mobile layout */}
        <main className={`flex-1 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'ml-0 lg:ml-64'} px-4 py-6 ${isSidebarOpen ? 'md:pl-72' : ''}`} style={{
        minHeight: 'calc(100vh - 144px)'
      }} data-id="element-1583">
          {children}
        </main>
      </div>
      <Footer data-id="element-1584" />
      {/* Overlay for mobile when sidebar is open */}
      {isSidebarOpen && <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleSidebar} aria-hidden="true" data-id="element-1585"></div>}
    </div>;
};